import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { AnyObject } from 'react-final-form';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import './SubCodeSection.scss';
import SubCodeModel from 'src/models/projectSettings/subCode/SubCodeSection';

import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';

import Jeffersonian from 'src/models/projectSettings/subCode/section_types/Jeffersonian';
import NonJeffersonian from 'src/models/projectSettings/subCode/section_types/NonJeffersonian';
import EasternTownship from 'src/models/projectSettings/subCode/section_types/EasternTownship';
import GatheringStation from 'src/models/projectSettings/subCode/section_types/GatheringStation';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import Card from 'src/components/common/Card.redesign';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';

import { EasternTownshipTypeArray } from 'src/models/EasternTownshipType';
// import CardContainer from 'src/components/common/CardContainer.redesign';
import PagedResultSet from 'src/models/common/PagedResultSet';
import PagedItemList from 'src/components/common/PagedItemList';
import { ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import * as _ from 'lodash';

const SubCodeSection = () => {
    const { register, handleSubmit, errors, reset } = useForm();
    const { get, response, put, post } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const [formData, setFormData] = useState(null);
    const [projectSCD, setProjectSCD] = useState<PagedResultSet<SubCodeModel>>(undefined);
    const [subCode, setSubCode] = useState<SubCodeModel>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(undefined);
    const [editListener, setEditListener] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const [subCodeListDataOptions, setSubCodeListDataOptions] = useState<ODataOptions>(new ODataOptions());
    const [resetLastPage, setResetLastPage] = useState(false);

    const easternTownshipTypes = EasternTownshipTypeArray();

    useEffect(() => {
        resetPagingOptions();
        // getProjectSCD(subCodeListDataOptions.toString());
    }, []);

    useEffect(() => {
        reset(formData);
    }, [formData]);

    const getProjectSCD = async (dataOptions: string, nextPage: boolean) => {
        setIsLoading(true);
        const result = await get(`/v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/subCode${dataOptions}`);

        if (response.ok) {
            if (nextPage === true) {
                let tempResult = _.cloneDeep(projectSCD);
                tempResult.pageNumber += 1;
                tempResult.items = [...tempResult.items].concat([...result.items]);
                setProjectSCD(tempResult);
            } else {
                setProjectSCD(result);
            }
            setIsLoading(false);
        } else {
            console.log('bad request', response, dataOptions);
        }
    };

    useEffect(() => {
        if (subCodeListDataOptions.getPageNumber() > 0) {
            setIsLoading(true);
            getProjectSCD(subCodeListDataOptions.toString(), subCodeListDataOptions.getIsNextPage());
            setResetLastPage(true);
        }
    }, [subCodeListDataOptions]);

    const resetPagingOptions = () => {
        const tableConfig: ITableConfig = {
            rowCount: 20,
            idField: 'SectionId',
            pageNumber: 1,
            columns: []
        };
        let newDataOptions = ODataOptions.from(tableConfig);
        newDataOptions.addSkipTop(0, 20);
        newDataOptions.addOrderBy('SectionId', ODataOrderByDirection.Ascending);
        setSubCodeListDataOptions(newDataOptions);
    };

    const prepFormData = (sc: SubCodeModel) => {
        let data: SubCodeModel = {
            id: sc ? sc?.id : 0,
            calmsSectionNo: sc ? sc?.calmsSectionNo : 0,
            sectionSurveyType: sc ? sc?.sectionSurveyType : '',
            nonJeffersonian: sc ? sc?.nonJeffersonian : null,
            jeffersonian: sc ? sc?.jeffersonian : null,
            eastern: sc ? sc?.eastern : null,
            gatheringStationArea: sc ? sc?.gatheringStationArea : null,
        };

        setSubCode(data);
        setFormData(data);
    };

    // Resets data of componet so no "previous" data would overlap
    const resetComponentData = () => {
        let defaultSC: SubCodeModel = {
            id: null,
            calmsSectionNo: null,
            sectionSurveyType: '',
            nonJeffersonian: null,
            jeffersonian: null,
            eastern: null,
            gatheringStationArea: null,
        };
        prepFormData(defaultSC);
        reset();
        setSubCode(undefined);
        setSelectedType('');
    };

    // Sets the state for user selected survey type
    const handleSurveyTypeChange = (event: AnyObject) => {
        setSelectedType(event.target.value);
    };

    // Renders survey type bases on selectedType state
    const sectionCard = (surveyType: string) => {
        switch (surveyType) {
            case 'nonJeffersonian':
                return (
                    <>
                        <div className="borderTopSG surveyTypeDimensions">
                            <div className="flex gap__2 padTop__2">
                                <div className="input-width">
                                    <RHFInputBox label="Block" name="nonJeffersonian.block" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Township" name="nonJeffersonian.township" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Section" name="nonJeffersonian.section" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Fraction" name="nonJeffersonian.fraction" register={register} errors={errors} />
                                </div>
                            </div>
                            <div className="flex gap__2 padTop__2">
                                <div className="input-width">
                                    <RHFInputBox label="Abstract" name="nonJeffersonian.abstract" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Survey" name="nonJeffersonian.survey" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFSelect
                                        label="Survey Type"
                                        name="nonJeffersonian.surveyType"
                                        options={[
                                            { value: 'semiMonthly', label: 'Survey' },
                                            { value: 'monthly', label: 'None' },
                                        ]}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                            </div>
                            <div className="flex gap__2" style={{ justifyContent: 'flex-end', alignItems: 'center', padding: '2rem 0' }}>
                                <div className="input-width">
                                    <p>Section Assigned to: </p>
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="TSD No." name="nonJeffersonian.tsdNo" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="tsdName" name="nonJeffersonian.name" register={register} errors={errors} />
                                </div>
                            </div>
                            <div className="borderTopSG" style={{ marginTop: '2rem' }} />
                        </div>
                    </>
                );
            case 'jeffersonian':
                return (
                    <>
                        <div className="padTop__2 padBtm__2 borderTopSG surveyTypeDimensions">
                            <div className="flex gap__2">
                                <div className="input-width">
                                    <RHFInputBox label="Section" name="jeffersonian.section" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Township" name="jeffersonian.township" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Range" name="jeffersonian.range" register={register} errors={errors} />
                                </div>
                            </div>

                            <div className="flex gap__2 padTop__2">
                                <div className="input-width">
                                    <p>Section Assigned to: </p>
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="TSD No." name="jeffersonian.tsdNo" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="tsdName" name="jeffersonian.tsdName" register={register} errors={errors} />
                                </div>
                            </div>
                            <div className="borderTopSG" style={{ marginTop: '2rem' }} />
                        </div>
                    </>
                );
            case 'eastern':
                return (
                    <>
                        <div className=" padTop__2 padBtm__2 borderTopSG surveyTypeDimensions">
                            <div className="flex gap__2">
                                <div className="input-width">
                                    <RHFSelect
                                        label="Eastern Township Type"
                                        name="eastern.easternTownshipTypeId"
                                        options={easternTownshipTypes.map((type: AnyObject) => {
                                            return { value: type.id, label: `${type.name}` };
                                        })}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="District/Township Name" name="eastern.unitName" register={register} errors={errors} />
                                </div>

                                <div className="input-width">
                                    <RHFInputBox label="Tract" name="eastern.tract" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="Municipality" name="eastern.municipality" register={register} errors={errors} />
                                </div>
                            </div>

                            <div className="flex gap__2 padTop__2">
                                <div className="input-width">
                                    <p>Tract Assigned to: </p>
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="TSD No." name="eastern.tsdNo" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="TSD Name" name="eastern.tsdName" register={register} errors={errors} />
                                </div>
                            </div>
                            <div className="borderTopSG" style={{ marginTop: '2rem' }} />
                        </div>
                    </>
                );
            case 'gatheringStationArea':
                return (
                    <>
                        <div className="borderTopSG surveyTypeDimensions padBtm__2">
                            <div className="flex padTop__2 padBtm__2">
                                <div className="input-width">
                                    <RHFInputBox label="GS No." name="gatheringStationArea.gsNo" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="GS Name" name="gatheringStationArea.gsName" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="GS Field" name="gatheringStationArea.gsField" register={register} errors={errors} />
                                </div>
                                <div className="input-width">
                                    <RHFInputBox label="GS Type" name="gatheringStationArea.gsType" register={register} errors={errors} />
                                </div>
                            </div>
                            <div className="borderTopSG" style={{ marginTop: '2rem' }} />
                        </div>
                    </>
                );
            case 'unit':
                return (
                    <>
                        <div className="borderTopSG borderBtmSG surveyTypeDimensions">
                            <RHFTextArea label="Unit Free Form" name="unit.freeForm" />
                        </div>
                    </>
                );
            case '':
                return <div className="surveyTypeDimensions" />;
            default:
                break;
        }
        return <></>;
    };

    // const handleUIChange = () => {
    //     if (selectedType === 'nonJeffersonian') {
    //         return { animation: 'nJStyle 450ms ease-in-out' };
    //     } else if (selectedType === 'jeffersonian') {
    //         return { animation: 'jStyle 450ms ease-in-out' };
    //     } else if (selectedType === 'eastern') {
    //         return { animation: 'eStStyle 450ms ease-in-out' };
    //     } else if (selectedType === 'gatheringStation') {
    //         return { animation: 'gSStyle 450ms ease-in-out' };
    //     } else {
    //         return { animation: 'noSelect 470ms ease-in-out', animationFillMode: 'forwards' };
    //     }
    // };

    // Renders a list of Sub Codes
    const renderSubCode = () => {
        if (!isLoading && projectSCD?.items?.length > 0) {
            return projectSCD?.items.map((sc: SubCodeModel) => {
                const handleEditSubCode = () => {
                    setEditListener(true);
                    setShowForm(true);
                    prepFormData(sc);
                    setSelectedType(sc?.sectionSurveyType);
                };

                const handleAltSubCodeRender = () => {
                    dialogContext.project.addAltSubCode(sc.id);
                };

                return (
                    <Card
                        badge={sc?.id ? `${appContext.clientNumber} . ${appContext.projectNumber} . ${sc?.id}` : ''}
                        key={sc?.id}
                        style={{ margin: 'auto 0 -1px 0' }}
                    >
                        <div className="flex " style={{ justifyContent: 'space-between', margin: '0 5rem 1rem 5rem' }}>
                            <div>
                                {!!sc?.nonJeffersonian ? <p>Non-Jeffersonian</p> : <></>}
                                {!!sc?.jeffersonian ? <p>Jeffersonian</p> : <></>}
                                {!!sc?.eastern ? <p>Eastern States</p> : <></>}
                                {!!sc?.gatheringStationArea ? <p>Gathering Station</p> : <></>}
                            </div>
                            <div className="flex gap__2">
                                <Button text="Edit Sub Code" type="submit" onClick={handleEditSubCode} />
                                <Button text="View Alt Sub-Codes" type="submit" onClick={handleAltSubCodeRender} />
                            </div>
                        </div>
                    </Card>
                );
            });
        }

        if (!isLoading && projectSCD?.items?.length === 0) {
            return (
                <Card style={{ textAlign: 'center' }}>
                    <p>No Sub Codes Available.</p>
                </Card>
            );
        }

        return isLoading && <LoadingSpinner />;
    };

    const handleAddSubCode = () => {
        setShowForm(true);
    };

    const handleCancelBtn = () => {
        resetComponentData();
        setShowForm(false);
        setEditListener(false);
    };

    // Renders the form to add or edit a Sub Code
    const renderForm = () => {
        return (
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <RHFLegend title="Survey Types" />
                    <div className="flex gap__2" style={{ marginTop: '2rem' }}>
                        {/* <div className="input-width">
                                    <RHFInputBox name="calmsSectionNo" label="Calms Section No" register={register} errors={errors} />
                                </div> */}
                        <div className="input-width">
                            <RHFSelect
                                label="Section Survey Types"
                                name="sectionSurveyType"
                                options={[
                                    { value: 'jeffersonian', label: 'Jeffersonian (SEC-TWN-RNG)' },
                                    { value: 'nonJeffersonian', label: 'Non-Jeffersonian (EX: TX)' },
                                    { value: 'eastern', label: 'Eastern States' },
                                    { value: 'gatheringStationArea', label: 'Gathering Station' },
                                    { value: 'unit', label: 'Unit/Area' },
                                ]}
                                register={register}
                                errors={errors}
                                onChange={handleSurveyTypeChange}
                                disabled={editListener ? true : false}
                                style={editListener ? { cursor: 'not-allowed' } : {}}
                            />
                        </div>
                        <div>{sectionCard(selectedType)}</div>
                    </div>

                    <div className="flex gap__2" style={{ marginTop: '2rem' }}>
                        <Button text="Submit" intent="primary" type="submit" />
                        <Button text="Cancel" type="button" onClick={handleCancelBtn} />
                    </div>
                </form>
            </>
        );
    };

    // Format submitted data to creat or update a sub code
    const formatData = (data: AnyObject) => {
        let newData: SubCodeModel = {
            id: 0,
            calmsSectionNo: 0,
            sectionSurveyType: '',
            nonJeffersonian: new NonJeffersonian(),
            jeffersonian: new Jeffersonian(),
            eastern: new EasternTownship(),
            gatheringStationArea: new GatheringStation(),
        };

        newData.id = subCode?.id ? parseInt(subCode?.id + '', 10) : 0;
        newData.calmsSectionNo = data.calmsSectionNo ? parseInt(data.calmsSectionNo + '', 10) : 0;
        newData.sectionSurveyType = data.sectionSurveyType ? data.sectionSurveyType : '';

        if (data.sectionSurveyType === 'nonJeffersonian') {
            // NonJeffersonian data sanitization
            newData.nonJeffersonian.id = subCode?.nonJeffersonian?.id ? parseInt(subCode?.nonJeffersonian?.id + '', 10) : 0;
            newData.nonJeffersonian.nonJeffersonianTownshipId = subCode?.nonJeffersonian?.nonJeffersonianTownshipId
                ? parseInt(subCode?.nonJeffersonian?.nonJeffersonianTownshipId + '', 10)
                : 0;
            newData.nonJeffersonian.abstract = data.nonJeffersonian.abstract ? data.nonJeffersonian.abstract : '';
            newData.nonJeffersonian.survey = data.nonJeffersonian.survey ? data.nonJeffersonian.survey : '';
            newData.nonJeffersonian.sectionSurveyType = data.nonJeffersonian.surveyType ? data.nonJeffersonian.surveyType : '';
            newData.nonJeffersonian.block = data.nonJeffersonian.block ? data.nonJeffersonian.block : '';
            newData.nonJeffersonian.section = data.nonJeffersonian.section ? parseInt(data.nonJeffersonian.section + '', 10) : 0;
            newData.nonJeffersonian.fraction = data.nonJeffersonian.fraction ? parseFloat(data.nonJeffersonian.fraction + '') : 0;
            newData.nonJeffersonian.tsdNo = data.nonJeffersonian.tsdNo ? parseInt(data.nonJeffersonian.tsdNo + '', 10) : 0;
            newData.nonJeffersonian.tsdName = data.nonJeffersonian.tsdName ? data.nonJeffersonian.tsdName : '';

            newData.jeffersonian = !!subCode?.jeffersonian ? subCode?.jeffersonian : null;
            newData.eastern = !!subCode?.eastern ? subCode?.eastern : null;
            newData.gatheringStationArea = !!subCode?.gatheringStationArea ? subCode?.gatheringStationArea : null;
        }

        if (data.sectionSurveyType === 'jeffersonian') {
            // Jeffersonian data sanitization
            newData.jeffersonian.id = subCode?.jeffersonian?.id ? parseInt(subCode?.jeffersonian?.id + '', 10) : 0;
            newData.jeffersonian.townshipId = subCode?.jeffersonian?.townshipId ? parseInt(subCode?.jeffersonian?.townshipId + '', 10) : 0;
            newData.jeffersonian.range = data.jeffersonian.range ? parseInt(data.jeffersonian.range + '', 10) : 0;
            newData.jeffersonian.section = data.jeffersonian.section ? parseInt(data.jeffersonian.section + '', 10) : 0;
            newData.jeffersonian.township = data.jeffersonian.township ? parseInt(data.jeffersonian.township + '', 10) : 0;
            newData.jeffersonian.tsdNo = data.jeffersonian.tsdNo ? parseInt(data.jeffersonian.tsdNo + '', 10) : 0;
            newData.jeffersonian.tsdName = data.jeffersonian.tsdName ? data.jeffersonian.tsdName : '';

            newData.nonJeffersonian = !!subCode?.nonJeffersonian ? subCode?.nonJeffersonian : null;
            newData.eastern = !!subCode?.eastern ? subCode?.eastern : null;
            newData.gatheringStationArea = !!subCode?.gatheringStationArea ? subCode?.gatheringStationArea : null;
        }

        if (data.sectionSurveyType === 'eastern') {
            // EasternTonwship data sanitization
            newData.eastern.id = subCode?.eastern?.id ? parseInt(subCode?.eastern?.id + '', 10) : 0;
            newData.eastern.easternTownshipId = subCode?.eastern?.easternTownshipId ? parseInt(subCode?.eastern?.easternTownshipId + '', 10) : 0;
            newData.eastern.easternTownshipTypeId = data.eastern.easternTownshipTypeId ? parseInt(data.eastern.easternTownshipTypeId + '', 10) : 0;
            newData.eastern.unitName = data.eastern.unitName ? data.eastern.unitName : '';
            newData.eastern.tract = data.eastern.tract ? data.eastern.tract : '';
            newData.eastern.municipality = data.eastern.municipality ? data.eastern.municipality : '';
            newData.eastern.tsdNo = data.eastern.tsdNo ? parseInt(data.eastern.tsdNo + '', 10) : 0;
            newData.eastern.tsdName = data.eastern.tsdName ? data.eastern.tsdName : '';

            newData.jeffersonian = !!subCode?.jeffersonian ? subCode?.jeffersonian : null;
            newData.nonJeffersonian = !!subCode?.nonJeffersonian ? subCode?.nonJeffersonian : null;
            newData.gatheringStationArea = !!subCode?.gatheringStationArea ? subCode?.gatheringStationArea : null;
        }

        if (data.sectionSurveyType === 'gatheringStationArea') {
            // Gathering Station data sanitization
            newData.gatheringStationArea.id = subCode?.gatheringStationArea?.id ? parseInt(subCode?.gatheringStationArea?.id + '', 10) : 0;
            newData.gatheringStationArea.gatheringStationId = subCode?.gatheringStationArea?.gatheringStationId
                ? parseInt(subCode?.gatheringStationArea?.gatheringStationId + '', 10)
                : 0;
            newData.gatheringStationArea.areaNumber = data.gatheringStationArea.areaNumber
                ? parseInt(data.gatheringStationArea.areaNumber + '', 10)
                : 0;
            newData.gatheringStationArea.gsNo = data.gatheringStationArea.gsNo ? parseInt(data.gatheringStationArea.gsNo + '', 10) : 0;
            newData.gatheringStationArea.gsName = data.gatheringStationArea.gsName ? data.gatheringStationArea.gsName : '';
            newData.gatheringStationArea.gsField = data.gatheringStationArea.gsField ? data.gatheringStationArea.gsField : '';
            newData.gatheringStationArea.gsType = data.gatheringStationArea.gsType ? data.gatheringStationArea.gsType : '';

            newData.jeffersonian = !!subCode?.jeffersonian ? subCode?.jeffersonian : null;
            newData.nonJeffersonian = !!subCode?.nonJeffersonian ? subCode?.nonJeffersonian : null;
            newData.eastern = !!subCode?.eastern ? subCode?.eastern : null;
        }

        return newData;
    };

    const createRecord = async (data: SubCodeModel) => {
        const result = await post(`/v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/subCode`, data);

        if (response.ok) {
            console.log('Successfully created a new Sub Code record', result);
            resetComponentData();
            getProjectSCD(subCodeListDataOptions.toString(), false);
        }
    };

    const updateRecord = async (data: SubCodeModel) => {
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/Projects/${appContext.projectNumber}/subCode`, data);

        if (response.ok) {
            console.log('SBC - Updated current record', result);
            resetComponentData();
            getProjectSCD(subCodeListDataOptions.toString(), false);
        }
    };

    const onSubmit = (values: AnyObject) => {
        setShowForm(false);

        const newValues = formatData(values);

        if (!newValues?.id || newValues?.id === 0) {
            createRecord(newValues);
        } else {
            updateRecord(newValues);
        }
    };

    const getProjectSCDPagingDelegate = async (dataOptions: ODataOptions, resetSearch: boolean) => {
        getProjectSCD(dataOptions.toString(), dataOptions.getIsNextPage());
    };

    return (
        <div>
            <div style={{ marginBottom: '2rem' }}>
            <PagedItemList
                style={{ overflowY: 'scroll', maxHeight: '73vh' }}
                resultsLabelStyle={{ marginLeft: '1rem', marginRight: '2.1rem', background: 'gainsboro', border: '1px solid gainsboro', borderTopLeftRadius: '5px', borderTopRightRadius: '5px' }}
                resetLastPage={resetLastPage}
                ResetStateCallback={resetPagingOptions}
                dataOptions={subCodeListDataOptions}
                GetPagedItemList={getProjectSCDPagingDelegate}
                DisplayList={renderSubCode}
                orderByColumn={subCodeListDataOptions.getOrderByProperty() !== undefined ?
                    subCodeListDataOptions.getOrderByProperty() : 'Number'}
                orderByDirection={subCodeListDataOptions.getOrderByDirection() !== undefined ?
                    subCodeListDataOptions.getOrderByDirection() : ODataOrderByDirection.Descending}
                startingPage={1}
                rowsPerPage={projectSCD?.pageSize}
                totalRows={projectSCD?.totalRecords}
            />
            </div>

            <div>
                <div
                    style={
                        showForm
                            ? { animationName: 'openForm', animationDuration: '450ms', animationTimingFunction: 'lienar' }
                            : showForm === undefined
                            ? {}
                            : {
                                  animationName: 'closeForm',
                                  animationDuration: '450ms',
                                  animationTimingFunction: 'lienar',
                                  animationFillMode: 'forwards',
                              }
                    }
                >
                    {showForm && renderForm()}
                </div>
                {!showForm && (
                    <div className="flex gap__2" style={{ marginTop: '1rem', paddingTop: '2.5rem' }}>
                        <Button text="Add Sub Code" intent="primary" type="button" onClick={handleAddSubCode} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubCodeSection;



// WEBPACK FOOTER //
// ./src/components/project/sections/SubCodeSection.tsx